import { AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux'
import { syncAuthStatusStoreFromState } from 'src/queries/authStatus.interop'
import { resolveAuthStatusWrite } from 'src/queries/authStatus.writers'
import { authStatusStore } from 'src/queries/authStatusStore'
import { AuthTokens, tokenStore } from 'src/queries/tokenStore'
import {
  areTokensEmpty,
  resolveTokenWrite,
} from 'src/queries/tokenStore.writers'
import { RootState } from 'src/redux/createRootReducer'

/** Interop between Redux and the non-Redux tokenStore/authStatusStore write-through seam. See src/queries/AGENTS.md for the write-first vs. default-mirror contract. */

/** The five auth-token fields Redux owns, read off the auth slice. */
const tokensFromState = (state: RootState): AuthTokens => {
  const { jwt, refreshToken, cspJwt, cspRefreshToken, userSessionTokenId } =
    state.auth
  return { jwt, refreshToken, cspJwt, cspRefreshToken, userSessionTokenId }
}

/**
 * Mirror the current Redux auth token fields into `tokenStore`. `setTokens` diffs,
 * so an unchanged state is a no-op (no snapshot churn, no listener notify).
 */
export const syncTokenStoreFromState = (state: RootState): void => {
  tokenStore.setTokens(tokensFromState(state))
}

/**
 * Prime `tokenStore` from the store's current (SSR-seeded, hydrated) auth state.
 * Call once on the browser entry after the store is created and before the first
 * authed request is built — the write-through middleware only fires on subsequent
 * dispatches, so without this the initial hydrated bearer would be missing.
 */
export const primeTokenStore = (store: { getState: () => RootState }): void => {
  syncTokenStoreFromState(store.getState())
}

/**
 * Apply an inverted (write-first) token write to `tokenStore`: a full-clear result
 * routes through the authoritative `clearTokens`, anything else through `setTokens`
 * (both diff internally, so a no-op change neither swaps the snapshot nor notifies).
 */
const applyTokenWrite = (nextTokens: AuthTokens): void => {
  if (areTokensEmpty(nextTokens)) {
    tokenStore.clearTokens()
  } else {
    tokenStore.setTokens(nextTokens)
  }
}

/** Redux <-> tokenStore/authStatusStore write-through middleware; each store independently decides write-first vs. mirror per action. See src/queries/AGENTS.md. */
export const tokenStoreWriteThroughMiddleware: Middleware<
  Record<string, never>,
  RootState
> =
  (store: MiddlewareAPI<Dispatch<AnyAction>, RootState>) =>
  (next: Dispatch<AnyAction>) =>
  (action: AnyAction): unknown => {
    const tokenWrite = resolveTokenWrite(action)
    const authStatusWrite = resolveAuthStatusWrite(action)

    // Write-first (inverted flows): apply BEFORE Redux reduces so the reactive stores
    // lead the render; Redux then reduces as the parallel mirror.
    if (tokenWrite) {
      applyTokenWrite(tokenWrite(tokenStore.getSnapshot()))
    }
    if (authStatusWrite !== undefined) {
      authStatusStore.setAuthStatus(authStatusWrite)
    }

    const result = next(action)

    // Post-reduce mirror (default path): only for the store whose write was NOT
    // inverted for this action. A write-first action skips its own store's mirror so
    // the value no longer derives from `state.auth`; the other store still mirrors.
    if (!tokenWrite) {
      syncTokenStoreFromState(store.getState())
    }
    if (authStatusWrite === undefined) {
      syncAuthStatusStoreFromState(store.getState())
    }
    return result
  }
